import { create } from 'zustand';
import {
  archiveCollectionPoint,
  createCollectionPoint,
  listCollectionPoints,
  updateCollectionPoint,
} from '../services/collection-point.service';
import type {
  CollectionPointPagination,
  CollectionPointQuery,
  SupplierCollectionPoint,
  SupplierCollectionPointInput,
  SupplierCollectionPointUpdateInput,
} from '../types/supplier.type';

interface CollectionPointState {
  collectionPoints: SupplierCollectionPoint[];
  total: number;
  page: number;
  limit: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  clearError: () => void;
  fetchCollectionPoints: (query: CollectionPointQuery) => Promise<CollectionPointPagination | null>;
  createCollectionPoint: (
    organizationId: string,
    input: SupplierCollectionPointInput,
  ) => Promise<SupplierCollectionPoint | null>;
  updateCollectionPoint: (
    organizationId: string,
    collectionPointId: string,
    input: SupplierCollectionPointUpdateInput,
  ) => Promise<SupplierCollectionPoint | null>;
  archiveCollectionPoint: (organizationId: string, collectionPointId: string) => Promise<boolean>;
}

function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

export const useCollectionPointStore = create<CollectionPointState>((set) => ({
  collectionPoints: [],
  total: 0,
  page: 1,
  limit: 20,
  isLoading: false,
  isSaving: false,
  error: null,
  clearError: () => set({ error: null }),

  fetchCollectionPoints: async (query) => {
    set({ isLoading: true, error: null });
    try {
      const result = await listCollectionPoints(query);
      // `result.collection_points` (API's snake_case field) -- not spread directly, since this
      // state's field is `collectionPoints` (camelCase) and a blind `{...result}` spread would
      // set an unread `collection_points` key while `collectionPoints` itself stays `[]` forever.
      set({
        collectionPoints: result.collection_points,
        total: result.total,
        page: result.page,
        limit: result.limit,
        isLoading: false,
      });
      return result;
    } catch (error) {
      set({ isLoading: false, error: errorMessage(error, 'Failed to load collection points') });
      return null;
    }
  },

  createCollectionPoint: async (organizationId, input) => {
    set({ isSaving: true, error: null });
    try {
      const point = await createCollectionPoint(organizationId, input);
      set((state) => ({ collectionPoints: [point, ...state.collectionPoints], total: state.total + 1, isSaving: false }));
      return point;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to create collection point') });
      return null;
    }
  },

  updateCollectionPoint: async (organizationId, collectionPointId, input) => {
    set({ isSaving: true, error: null });
    try {
      const point = await updateCollectionPoint(organizationId, collectionPointId, input);
      set((state) => ({
        collectionPoints: state.collectionPoints.map((row) => row.id === collectionPointId ? point : row),
        isSaving: false,
      }));
      return point;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to update collection point') });
      return null;
    }
  },

  archiveCollectionPoint: async (organizationId, collectionPointId) => {
    set({ isSaving: true, error: null });
    try {
      await archiveCollectionPoint(organizationId, collectionPointId);
      set((state) => ({
        collectionPoints: state.collectionPoints.filter((row) => row.id !== collectionPointId),
        total: Math.max(0, state.total - 1),
        isSaving: false,
      }));
      return true;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to archive collection point') });
      return false;
    }
  },
}));
